import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Boxes, Plus, Trash2, Pencil, Save, X, Loader, Search,
  Truck, Package, Unlink, Building2, ArrowRightLeft
} from 'lucide-react'
import { apiClient } from '../config/api'

// Fleet management: customer-operable groups of vehicles/assets.
// Left panel: fleet list + create. Right panel: selected fleet's members
// with an add-vehicle picker.

interface Fleet {
  id: number
  name: string
  description?: string
  customer_id?: number
  customer_name?: string
  customer_code?: string
  member_count: number
  created_by?: string
  created_at?: string
}

interface FleetMember {
  membership_id: number
  vehicle_source: 'ASSET' | 'CONTAINER'
  vehicle_id: number
  vehicle_code: string
  license_plate?: string
  make?: string
  model?: string
  added_by?: string
  added_at?: string
}

interface VehicleLite {
  id: number
  vehicle_code: string
  vehicle_source: 'ASSET' | 'CONTAINER'
  license_plate?: string
  customer_name?: string
  tracking_status?: string
}

interface CustomerOption {
  id: number
  customer_code: string
  company_name: string
}

interface FleetGroupsPanelProps {
  vehicles: VehicleLite[]
  showMessage?: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void
}

const inputCls = 'w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none'

const FleetGroupsPanel: React.FC<FleetGroupsPanelProps> = ({ vehicles, showMessage }) => {
  const { t } = useTranslation()
  const [fleets, setFleets] = useState<Fleet[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedFleet, setSelectedFleet] = useState<Fleet | null>(null)
  const [members, setMembers] = useState<FleetMember[]>([])
  const [membersLoading, setMembersLoading] = useState(false)
  const [customers, setCustomers] = useState<CustomerOption[]>([])

  // Create form
  const [showCreate, setShowCreate] = useState(false)
  const [newName, setNewName] = useState('')
  const [newDescription, setNewDescription] = useState('')
  const [newCustomerId, setNewCustomerId] = useState('')
  const [createBusy, setCreateBusy] = useState(false)

  // Edit fleet
  const [editingFleet, setEditingFleet] = useState(false)
  const [editName, setEditName] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editCustomerId, setEditCustomerId] = useState('')
  const [editBusy, setEditBusy] = useState(false)

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<Fleet | null>(null)
  const [deleteBusy, setDeleteBusy] = useState(false)

  // Add member picker
  const [memberSearch, setMemberSearch] = useState('')
  const [addBusy, setAddBusy] = useState<string | null>(null)

  // One fleet per vehicle: memberships tell us each vehicle's current fleet
  // so the picker can warn before a move. Key is "SOURCE-id".
  const [membershipByVehicle, setMembershipByVehicle] = useState<globalThis.Map<string, { fleetId: number, fleetName: string }>>(new globalThis.Map())

  // Confirmation dialogs for member operations
  const [addTarget, setAddTarget] = useState<{ vehicle: VehicleLite, currentFleet?: { fleetId: number, fleetName: string } } | null>(null)
  const [removeTarget, setRemoveTarget] = useState<FleetMember | null>(null)
  const [removeBusy, setRemoveBusy] = useState(false)

  const loadMemberships = async () => {
    try {
      const res = await apiClient.get('/fleets/memberships')
      const map = new globalThis.Map<string, { fleetId: number, fleetName: string }>()
      for (const row of (res.data || [])) {
        map.set(`${row.vehicle_source}-${row.vehicle_id}`, { fleetId: row.fleet_id, fleetName: row.fleet_name })
      }
      setMembershipByVehicle(map)
    } catch {
      // Badges just won't show if the endpoint is unavailable
    }
  }

  const loadFleets = async () => {
    try {
      const res = await apiClient.get('/fleets')
      setFleets(res.data || [])
      // Keep selection in sync with fresh data
      if (selectedFleet) {
        const still = (res.data || []).find((f: Fleet) => f.id === selectedFleet.id)
        setSelectedFleet(still || null)
      }
    } catch (error) {
      console.error('Error loading fleets:', error)
      showMessage?.(t('fleets_load_failed', { defaultValue: 'Failed to load fleets' }), 'error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadFleets()
    loadMemberships()
    apiClient.get('/customers').then(res => setCustomers(res.data || [])).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadMembers = async (fleetId: number) => {
    try {
      setMembersLoading(true)
      const res = await apiClient.get(`/fleets/${fleetId}/members`)
      setMembers(res.data || [])
    } catch (error) {
      console.error('Error loading fleet members:', error)
      setMembers([])
    } finally {
      setMembersLoading(false)
    }
  }

  useEffect(() => {
    if (selectedFleet) loadMembers(selectedFleet.id)
    else setMembers([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFleet?.id])

  const handleCreate = async () => {
    if (!newName.trim()) return
    setCreateBusy(true)
    try {
      const res = await apiClient.post('/fleets', {
        name: newName.trim(),
        description: newDescription.trim() || undefined,
        customer_id: newCustomerId ? Number(newCustomerId) : null
      })
      showMessage?.(t('fleet_created', { defaultValue: 'Fleet "{{name}}" created', name: newName.trim() }), 'success')
      setShowCreate(false)
      setNewName(''); setNewDescription(''); setNewCustomerId('')
      await loadFleets()
      setSelectedFleet(res.data)
    } catch (error) {
      console.error('Create fleet error:', error)
      showMessage?.(t('fleet_create_failed', { defaultValue: 'Failed to create fleet' }), 'error')
    } finally {
      setCreateBusy(false)
    }
  }

  const startEditFleet = () => {
    if (!selectedFleet) return
    setEditName(selectedFleet.name)
    setEditDescription(selectedFleet.description || '')
    setEditCustomerId(selectedFleet.customer_id ? String(selectedFleet.customer_id) : '')
    setEditingFleet(true)
  }

  const handleEditSave = async () => {
    if (!selectedFleet || !editName.trim()) return
    setEditBusy(true)
    try {
      await apiClient.put(`/fleets/${selectedFleet.id}`, {
        name: editName.trim(),
        description: editDescription.trim() || null,
        customer_id: editCustomerId ? Number(editCustomerId) : null
      })
      showMessage?.(t('fleet_updated', { defaultValue: 'Fleet updated' }), 'success')
      setEditingFleet(false)
      await loadFleets()
    } catch (error) {
      console.error('Update fleet error:', error)
      showMessage?.(t('fleet_update_failed', { defaultValue: 'Failed to update fleet' }), 'error')
    } finally {
      setEditBusy(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    setDeleteBusy(true)
    try {
      await apiClient.delete(`/fleets/${deleteTarget.id}`)
      showMessage?.(t('fleet_deleted', { defaultValue: 'Fleet "{{name}}" deleted', name: deleteTarget.name }), 'success')
      if (selectedFleet?.id === deleteTarget.id) setSelectedFleet(null)
      setDeleteTarget(null)
      await loadFleets()
    } catch (error) {
      console.error('Delete fleet error:', error)
      showMessage?.(t('fleet_delete_failed', { defaultValue: 'Failed to delete fleet' }), 'error')
    } finally {
      setDeleteBusy(false)
    }
  }

  // Step 1: ask for confirmation (dialog explains add vs move)
  const requestAddMember = (vehicle: VehicleLite) => {
    const current = membershipByVehicle.get(`${vehicle.vehicle_source}-${vehicle.id}`)
    setAddTarget({ vehicle, currentFleet: current && current.fleetId !== selectedFleet?.id ? current : undefined })
  }

  // Step 2: confirmed — the API moves the vehicle if it was in another fleet
  const confirmAddMember = async () => {
    if (!selectedFleet || !addTarget) return
    const { vehicle } = addTarget
    const key = `${vehicle.vehicle_source}-${vehicle.id}`
    setAddBusy(key)
    try {
      const res = await apiClient.post(`/fleets/${selectedFleet.id}/members`, {
        vehicle_source: vehicle.vehicle_source,
        vehicle_id: vehicle.id
      })
      const movedFrom = (res.data?.moved_from_fleets || []) as { id: number, name: string }[]
      if (movedFrom.length > 0) {
        showMessage?.(t('moved_to_fleet', {
          defaultValue: '{{vehicle}} moved from {{from}} to {{to}}',
          vehicle: vehicle.vehicle_code, from: movedFrom.map(f => f.name).join(', '), to: selectedFleet.name
        }), 'success')
      } else {
        showMessage?.(t('added_to_fleet_named', { defaultValue: '{{vehicle}} added to fleet {{fleet}}', vehicle: vehicle.vehicle_code, fleet: selectedFleet.name }), 'success')
      }
      setAddTarget(null)
      await Promise.all([loadMembers(selectedFleet.id), loadFleets(), loadMemberships()])
    } catch (error: any) {
      const msg = error?.response?.data?.error || t('add_to_fleet_failed', { defaultValue: 'Failed to add vehicle to fleet' })
      showMessage?.(msg, 'error')
    } finally {
      setAddBusy(null)
    }
  }

  const confirmRemoveMember = async () => {
    if (!selectedFleet || !removeTarget) return
    const member = removeTarget
    setRemoveBusy(true)
    try {
      await apiClient.delete(`/fleets/${selectedFleet.id}/members/${member.vehicle_source}/${member.vehicle_id}`)
      showMessage?.(t('removed_from_fleet', { defaultValue: '{{vehicle}} removed from {{fleet}}', vehicle: member.vehicle_code, fleet: selectedFleet.name }), 'success')
      setRemoveTarget(null)
      await Promise.all([loadMembers(selectedFleet.id), loadFleets(), loadMemberships()])
    } catch (error) {
      console.error('Remove member error:', error)
      showMessage?.(t('remove_from_fleet_failed', { defaultValue: 'Failed to remove vehicle from fleet' }), 'error')
    } finally {
      setRemoveBusy(false)
    }
  }

  // Vehicles not yet in the selected fleet, filtered by search
  const q = memberSearch.trim().toLowerCase()
  const candidateVehicles = vehicles
    .filter(v => !members.some(m => m.vehicle_source === v.vehicle_source && Number(m.vehicle_id) === Number(v.id)))
    .filter(v => !q
      || v.vehicle_code.toLowerCase().includes(q)
      || (v.license_plate || '').toLowerCase().includes(q)
      || (v.customer_name || '').toLowerCase().includes(q))

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col md:flex-row min-h-0 p-6 gap-6">
      {/* ===== Left: fleet list ===== */}
      <div className="md:w-80 flex-shrink-0 flex flex-col min-h-0 bg-white rounded-lg border border-gray-200">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <h3 className="font-semibold text-gray-900 flex items-center space-x-2">
            <Boxes className="w-5 h-5 text-blue-600" />
            <span>{t('fleets', { defaultValue: 'Fleets' })}</span>
          </h3>
          <button
            onClick={() => setShowCreate(!showCreate)}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-1.5"
          >
            <Plus className="w-4 h-4" />
            <span>{t('new_fleet', { defaultValue: 'New fleet' })}</span>
          </button>
        </div>

        {showCreate && (
          <div className="p-4 border-b border-gray-200 bg-blue-50/50 space-y-2 flex-shrink-0">
            <input
              type="text" autoFocus value={newName} onChange={e => setNewName(e.target.value)}
              placeholder={t('fleet_name_placeholder', { defaultValue: 'Fleet name (e.g. Bamako Nord)' }) as string}
              className={inputCls}
            />
            <input
              type="text" value={newDescription} onChange={e => setNewDescription(e.target.value)}
              placeholder={t('fleet_desc_placeholder', { defaultValue: 'Description (optional)' }) as string}
              className={inputCls}
            />
            <select value={newCustomerId} onChange={e => setNewCustomerId(e.target.value)} className={inputCls}>
              <option value="">{t('no_customer_fleet', { defaultValue: 'No customer (internal fleet)' })}</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
            <div className="flex justify-end space-x-2">
              <button onClick={() => setShowCreate(false)} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
                {t('cancel')}
              </button>
              <button
                onClick={handleCreate}
                disabled={!newName.trim() || createBusy}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-1"
              >
                {createBusy ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                <span>{t('create', { defaultValue: 'Create' })}</span>
              </button>
            </div>
          </div>
        )}

        <div className="flex-1 min-h-0 overflow-y-auto">
          {fleets.length === 0 ? (
            <div className="p-8 text-center">
              <Boxes className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-700">{t('no_fleets_yet', { defaultValue: 'No fleets yet' })}</p>
              <p className="text-xs text-gray-500 mt-1">{t('no_fleets_yet_hint', { defaultValue: 'Create a fleet to group vehicles for a customer, a zone or an operation.' })}</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {fleets.map(fleet => (
                <button
                  key={fleet.id}
                  onClick={() => { setSelectedFleet(fleet); setEditingFleet(false) }}
                  className={`w-full text-left p-4 hover:bg-gray-50 transition-colors ${selectedFleet?.id === fleet.id ? 'bg-blue-50 border-l-4 border-blue-600' : 'border-l-4 border-transparent'}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900">{fleet.name}</span>
                    <span className="text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">{fleet.member_count}</span>
                  </div>
                  {fleet.customer_name && (
                    <div className="text-xs text-gray-500 mt-0.5 flex items-center space-x-1">
                      <Building2 className="w-3 h-3" />
                      <span>{fleet.customer_name}</span>
                    </div>
                  )}
                  {fleet.description && <div className="text-xs text-gray-400 mt-0.5 truncate">{fleet.description}</div>}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ===== Right: selected fleet detail ===== */}
      <div className="flex-1 min-h-0 flex flex-col bg-white rounded-lg border border-gray-200">
        {!selectedFleet ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Boxes className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-600">{t('select_fleet_hint', { defaultValue: 'Select a fleet to manage its vehicles' })}</p>
            </div>
          </div>
        ) : (
          <>
            {/* Fleet header */}
            <div className="p-4 border-b border-gray-200 flex-shrink-0">
              {editingFleet ? (
                <div className="space-y-2">
                  <input type="text" value={editName} onChange={e => setEditName(e.target.value)} className={inputCls} />
                  <input type="text" value={editDescription} onChange={e => setEditDescription(e.target.value)} placeholder={t('fleet_desc_placeholder', { defaultValue: 'Description (optional)' }) as string} className={inputCls} />
                  <select value={editCustomerId} onChange={e => setEditCustomerId(e.target.value)} className={inputCls}>
                    <option value="">{t('no_customer_fleet', { defaultValue: 'No customer (internal fleet)' })}</option>
                    {customers.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                  </select>
                  <div className="flex justify-end space-x-2">
                    <button onClick={() => setEditingFleet(false)} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">{t('cancel')}</button>
                    <button
                      onClick={handleEditSave}
                      disabled={!editName.trim() || editBusy}
                      className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center space-x-1"
                    >
                      {editBusy ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      <span>{t('save')}</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">{selectedFleet.name}</h3>
                    <p className="text-xs text-gray-500">
                      {selectedFleet.customer_name
                        ? `${selectedFleet.customer_name} (${selectedFleet.customer_code})`
                        : t('internal_fleet', { defaultValue: 'Internal fleet' })}
                      {selectedFleet.description ? ` — ${selectedFleet.description}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center space-x-1">
                    <button onClick={startEditFleet} className="p-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg" title={t('edit_fleet', { defaultValue: 'Edit fleet' }) as string}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => setDeleteTarget(selectedFleet)} className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg" title={t('delete_fleet', { defaultValue: 'Delete fleet' }) as string}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Members + add picker */}
            <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-2 gap-0 lg:divide-x divide-gray-200">
              {/* Members */}
              <div className="flex flex-col min-h-0">
                <div className="px-4 py-3 border-b border-gray-100 flex-shrink-0">
                  <h4 className="text-sm font-medium text-gray-700">
                    {t('fleet_members', { defaultValue: 'Vehicles in this fleet' })} ({members.length})
                  </h4>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-2">
                  {membersLoading ? (
                    <div className="py-8 text-center"><Loader className="w-6 h-6 animate-spin text-blue-600 mx-auto" /></div>
                  ) : members.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-8">{t('fleet_empty', { defaultValue: 'No vehicles in this fleet yet. Add them from the right panel.' })}</p>
                  ) : (
                    members.map(m => (
                      <div key={m.membership_id} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                        <div className="flex items-center space-x-2 min-w-0">
                          {m.vehicle_source === 'ASSET' ? <Truck className="w-4 h-4 text-blue-600 flex-shrink-0" /> : <Package className="w-4 h-4 text-blue-600 flex-shrink-0" />}
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{m.vehicle_code}</p>
                            <p className="text-xs text-gray-500 truncate">{[m.make, m.model].filter(Boolean).join(' ')}{m.license_plate && m.license_plate !== 'N/A' ? ` • ${m.license_plate}` : ''}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => setRemoveTarget(m)}
                          className="text-orange-600 hover:text-orange-800 p-1.5 rounded hover:bg-orange-50 transition-colors flex-shrink-0"
                          title={t('remove_from_fleet_btn', { defaultValue: 'Remove from fleet' }) as string}
                        >
                          <Unlink className="w-4 h-4" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Add vehicles */}
              <div className="flex flex-col min-h-0 border-t lg:border-t-0 border-gray-200">
                <div className="px-4 py-3 border-b border-gray-100 flex-shrink-0">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">{t('add_vehicles', { defaultValue: 'Add vehicles' })}</h4>
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={memberSearch}
                      onChange={e => setMemberSearch(e.target.value)}
                      placeholder={t('search_vehicles_placeholder', { defaultValue: 'Search by code, plate or customer…' }) as string}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-2">
                  {candidateVehicles.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-8">
                      {q ? t('no_vehicle_match', { defaultValue: 'No vehicle matches your search' }) : t('all_vehicles_in_fleet', { defaultValue: 'All vehicles are already in this fleet' })}
                    </p>
                  ) : (
                    candidateVehicles.map(v => {
                      const key = `${v.vehicle_source}-${v.id}`
                      const currentFleet = membershipByVehicle.get(key)
                      const isMove = !!currentFleet && currentFleet.fleetId !== selectedFleet.id
                      return (
                        <div key={key} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-3 py-2 hover:border-gray-300">
                          <div className="flex items-center space-x-2 min-w-0">
                            {v.vehicle_source === 'ASSET' ? <Truck className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <Package className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{v.vehicle_code}</p>
                              <p className="text-xs text-gray-500 truncate">{v.customer_name || t('no_customer', { defaultValue: 'No customer' })}</p>
                              {isMove && (
                                <span className="inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200">
                                  <Boxes className="w-2.5 h-2.5" />
                                  {t('currently_in_fleet', { defaultValue: 'In fleet {{fleet}}', fleet: currentFleet.fleetName })}
                                </span>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => requestAddMember(v)}
                            disabled={addBusy === key}
                            className={`px-2.5 py-1 text-xs font-medium border rounded-lg transition-colors disabled:opacity-50 flex items-center space-x-1 flex-shrink-0 ${
                              isMove
                                ? 'text-amber-700 bg-amber-50 border-amber-200 hover:bg-amber-100'
                                : 'text-blue-700 bg-blue-50 border-blue-200 hover:bg-blue-100'
                            }`}
                          >
                            {addBusy === key ? <Loader className="w-3 h-3 animate-spin" /> : isMove ? <ArrowRightLeft className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                            <span>{isMove ? t('move', { defaultValue: 'Move' }) : t('add', { defaultValue: 'Add' })}</span>
                          </button>
                        </div>
                      )
                    })
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add / Move confirmation */}
      {addTarget && selectedFleet && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 w-full max-w-md mx-4">
            <div className="flex items-start space-x-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${addTarget.currentFleet ? 'bg-amber-100' : 'bg-blue-100'}`}>
                {addTarget.currentFleet
                  ? <ArrowRightLeft className="w-5 h-5 text-amber-600" />
                  : <Plus className="w-5 h-5 text-blue-600" />}
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">
                  {addTarget.currentFleet
                    ? t('move_to_fleet_title', { defaultValue: 'Move vehicle to another fleet' })
                    : t('add_to_fleet_title', { defaultValue: 'Add vehicle to fleet' })}
                </h3>
                <p className="mt-2 text-sm text-gray-600">
                  {addTarget.currentFleet
                    ? t('move_to_fleet_confirm', {
                        defaultValue: '{{vehicle}} is currently in fleet "{{from}}". Move it to "{{to}}"? It will leave "{{from}}".',
                        vehicle: addTarget.vehicle.vehicle_code,
                        from: addTarget.currentFleet.fleetName,
                        to: selectedFleet.name
                      })
                    : t('add_to_fleet_confirm', {
                        defaultValue: 'Add {{vehicle}} to fleet "{{fleet}}"?',
                        vehicle: addTarget.vehicle.vehicle_code,
                        fleet: selectedFleet.name
                      })}
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setAddTarget(null)}
                disabled={addBusy !== null}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t('cancel')}
              </button>
              <button
                onClick={confirmAddMember}
                disabled={addBusy !== null}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 ${
                  addTarget.currentFleet ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {addBusy !== null
                  ? t('processing', { defaultValue: 'Processing…' })
                  : addTarget.currentFleet
                    ? t('move', { defaultValue: 'Move' })
                    : t('add', { defaultValue: 'Add' })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove member confirmation */}
      {removeTarget && selectedFleet && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 w-full max-w-md mx-4">
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Unlink className="w-5 h-5 text-orange-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">{t('remove_from_fleet_btn', { defaultValue: 'Remove from fleet' })}</h3>
                <p className="mt-2 text-sm text-gray-600">
                  {t('remove_from_fleet_confirm', {
                    defaultValue: 'Remove {{vehicle}} from fleet "{{fleet}}"? The vehicle will no longer belong to any fleet.',
                    vehicle: removeTarget.vehicle_code,
                    fleet: selectedFleet.name
                  })}
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setRemoveTarget(null)}
                disabled={removeBusy}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t('cancel')}
              </button>
              <button
                onClick={confirmRemoveMember}
                disabled={removeBusy}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50"
              >
                {removeBusy ? t('processing', { defaultValue: 'Processing…' }) : t('remove', { defaultValue: 'Remove' })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 w-full max-w-md mx-4">
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">{t('delete_fleet', { defaultValue: 'Delete fleet' })}</h3>
                <p className="mt-2 text-sm text-gray-600">
                  {t('delete_fleet_confirm', {
                    defaultValue: 'Delete fleet "{{name}}" ({{count}} vehicles)? The vehicles themselves are not deleted, only the grouping.',
                    name: deleteTarget.name,
                    count: deleteTarget.member_count
                  })}
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleteBusy}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteBusy}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleteBusy ? t('processing', { defaultValue: 'Processing…' }) : t('delete', { defaultValue: 'Delete' })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default FleetGroupsPanel
